// Copyright 2025 Sorama B.V.

import { autoinject } from "aurelia-dependency-injection";
import { ReportableMeasurement } from "datatypes/ReportableMeasurement";
import { GasType, SorXLDData } from "datatypes/SorXMeasurement";
import { Energy } from "units/Energy";
import { MassFlow } from "units/MassFlow";
import { UnitValue } from "units/Unit";
import { UnitConverters } from "units/UnitConverters";
import { Volume, VolumeCost } from "units/Volume";
import { FlowEffort, VolumeFlow } from "units/VolumeFlow";

@autoinject
export class SorXLDComputationService {
    private static notAvailableString = "0.00";

    public static estimatedKWH(report: ReportableMeasurement<SorXLDData>): UnitValue {
        const {
            tagLeakRate: leakRate,
            tagLeakPowerRatio: powerToFlowRatio,
            tagLeakOperatingHours,
            tagLeakGasType,
        } = report.data;
        // Convert leak rate to l/min (with gas type context for g/h → l/min conversion)
        const leakRateNormalized = leakRate.toUnit(VolumeFlow.litersPerMinute, { gasType: tagLeakGasType });

        // Calculate estimated power in kW: power ratio (kW/(l/min)) × leak rate (l/min)
        const powerToFlowRatioNormalized = powerToFlowRatio.toUnit(FlowEffort.kwPerliterMinute);

        const estimatedPowerKW = powerToFlowRatioNormalized * leakRateNormalized;
        const totalKWH = estimatedPowerKW * tagLeakOperatingHours;

        return new UnitValue(totalKWH, Energy.kiloWattHours);
    }

    public static estimatedLeakCosts(report: ReportableMeasurement<SorXLDData>): UnitValue {
        const {
            tagLeakCost: leakCost,
            tagLeakElectricityCost: electricityCost,
            tagLeakRate: leakRate,
            tagLeakGasCost: gasCost,
            tagLeakPowerRatio: powerRatio,
            tagLeakOperatingHours: operatingHours,
            tagLeakGasType: gasType,
        } = report.data;

        const currencyUnit = leakCost.unit;

        // Normalize leak rate to l/min (with gas type context for g/h → l/min conversion)
        const leakRateLpm = leakRate.toUnit(VolumeFlow.litersPerMinute, { gasType });

        // Normalize power ratio to kW/(l/min) to match the leak rate unit
        const powerRatioNormalized = powerRatio.toUnit(FlowEffort.kwPerliterMinute);

        // Normalize gas cost to $/L to match the leak rate volume unit
        const gasCostPerLiter = gasCost.toUnit(VolumeCost.euroPerLiter);

        // Gas cost = leak rate (l/min) × 60 (min/hour) × gas price ($/L) × operating hours (hours/year)
        const gasCostTotal = leakRateLpm * 60 * gasCostPerLiter * operatingHours;

        // Electric cost = electricity price × power ratio (kW/(l/min)) × leak rate (l/min) × operating hours
        const electricCostTotal = electricityCost.value * powerRatioNormalized * leakRateLpm * operatingHours;

        return new UnitValue(gasCostTotal + electricCostTotal, currencyUnit);
    }

    private static getEmissionFactorForGas(gasType: GasType): number | null {
        switch (gasType) {
            case "Air":
                return 0.0;
            case "Argon":
                return 0.0016;
            case "Helium":
                return 0.0;
            case "Hydrogen":
                return 0.012;
            case "Nitrogen":
                return 0.0013;
            case "NaturalGas":
                return 0.0019;
            case "Methane":
                return 0.0025;
            case "Oxygen":
                return 0.0;
            case "Other":
                return 0.001;
            default:
                return null;
        }
    }

    public static estimatedCO2Emission(report: ReportableMeasurement<SorXLDData>): number {
        const { tagLeakRate: leakRate, tagLeakGasType: gasType, tagLeakOperatingHours: operatingHours } = report.data;

        const leakRateLpm = (leakRate.value * leakRate.unit.conversion) / Volume.liters.conversion;
        const totalLeakedLitersPerYear = leakRateLpm * (operatingHours * 60);

        const emissionFactor = this.getEmissionFactorForGas(gasType);

        const emissionsKg = totalLeakedLitersPerYear * emissionFactor;

        if (emissionsKg === null || isNaN(emissionsKg)) {
            return 0;
        }

        return emissionsKg;
    }

    public static getLeakRate(report: ReportableMeasurement<SorXLDData>, targetUnit: string): UnitValue {
        const { tagLeakRate, tagLeakGasType } = report.data;

        if (!tagLeakRate) {
            return new UnitValue(0, tagLeakRate?.unit);
        }

        // Get L/min (air equivalent) — if stored as g/h, convert back using gas type context
        const lpm = tagLeakRate.toUnit(VolumeFlow.litersPerMinute, { gasType: tagLeakGasType });

        let value: number;

        if (targetUnit === "g/hour") {
            // Convert l/min (air) to g/h using the target gas type density
            value = UnitConverters.convertVolumeFlowToMassFlow(VolumeFlow.litersPerMinute, MassFlow.gramsPerHour, lpm, {
                gasType: tagLeakGasType,
            });
        } else {
            if (tagLeakGasType === "Air") {
                value = lpm;
            } else {
                // Convert l/min (air) -> g/h (air) -> l/min (target gas)
                const gphAir = UnitConverters.convertVolumeFlowToMassFlow(
                    VolumeFlow.litersPerMinute,
                    MassFlow.gramsPerHour,
                    lpm,
                    { gasType: "Air" }
                );
                value = UnitConverters.convertMassFlowToVolumeFlow(
                    MassFlow.gramsPerHour,
                    VolumeFlow.litersPerMinute,
                    gphAir,
                    { gasType: tagLeakGasType }
                );
            }
        }

        const unit = targetUnit === "g/hour" ? MassFlow.gramsPerHour : VolumeFlow.litersPerMinute;
        return new UnitValue(value, unit);
    }
}
